/**
 * Dynamic Theme Configuration สำหรับ user-modern
 * ใช้แนวทางเดียวกับ user-classic: โครงสร้าง index.html เหมือนกัน ต่างแค่ assets
 * Assets มาจาก cdn-assets ตาม folder (HuayToday-LightBlue, HuayToday-Blue, HuayToday-Red)
 */

export interface ThemeMetadata {
  id: string
  name: string
  displayName: string
  folder: string
  description?: string
  colorScheme: {
    primary: string
    secondary: string
  }
  assets: {
    styles: string[]
    scripts: string[]
    favicon: string
    logo: string
  }
  status: 'active' | 'beta' | 'deprecated'
}

/** โฟลเดอร์ theme ตรงกับ cdn-assets (HuayToday-Blue, HuayToday-LightBlue, HuayToday-Red) */
const MODERN_THEME_FOLDERS = {
  LIGHT_BLUE: 'HuayToday-LightBlue',
  BLUE: 'HuayToday-Blue',
  RED: 'HuayToday-Red',
} as const

/** รายการ CSS ตรงลำดับกับ template + โครงสร้าง cdn-assets (path ใต้ folder: /css/...) */
const MODERN_THEME_STYLES = [
  '/css/style.css',
  '/css/bootstrap.min.css',
  '/css/animate.min.css',
  '/css/fontawesome-all.min.css',
]

/** Central Theme Registry - 3 themes สำหรับ user-modern (Blue, LightBlue, Red) */
export const AVAILABLE_THEMES: Record<string, ThemeMetadata> = {
  'huay-today-lightblue': {
    id: 'huay-today-lightblue',
    name: 'HuayTodayLightBlue',
    displayName: 'HuayToday Light Blue',
    folder: MODERN_THEME_FOLDERS.LIGHT_BLUE,
    description: 'ธีมสไตล์โมเดิร์น โทนฟ้าอ่อน',
    colorScheme: {
      primary: '#0ea5e9',
      secondary: '#0284c7',
    },
    assets: {
      styles: [...MODERN_THEME_STYLES],
      scripts: [],
      favicon: '/img/favicon.ico',
      logo: '/img/logo-m.png',
    },
    status: 'active',
  },
  'huay-today-blue': {
    id: 'huay-today-blue',
    name: 'HuayTodayBlue',
    displayName: 'HuayToday Blue',
    folder: MODERN_THEME_FOLDERS.BLUE,
    description: 'ธีมสไตล์โมเดิร์น โทนน้ำเงิน',
    colorScheme: {
      primary: '#1e3a8a',
      secondary: '#1e40af',
    },
    assets: {
      styles: [...MODERN_THEME_STYLES],
      scripts: [],
      favicon: '/img/favicon.ico',
      logo: '/img/logo-m.png',
    },
    status: 'active',
  },
  'huay-today-red': {
    id: 'huay-today-red',
    name: 'HuayTodayRed',
    displayName: 'HuayToday Red',
    folder: MODERN_THEME_FOLDERS.RED,
    description: 'ธีมสไตล์โมเดิร์น โทนแดง',
    colorScheme: {
      primary: '#dc2626',
      secondary: '#991b1b',
    },
    assets: {
      styles: [...MODERN_THEME_STYLES],
      scripts: [],
      favicon: '/img/favicon.ico',
      logo: '/img/logo-m.png',
    },
    status: 'active',
  },
}

export const getActiveThemes = (): ThemeMetadata[] => {
  return Object.values(AVAILABLE_THEMES).filter((t) => t.status === 'active')
}

export const getThemeById = (id: string): ThemeMetadata | null => {
  return AVAILABLE_THEMES[id] ?? null
}

export const getThemeIds = (): string[] => Object.keys(AVAILABLE_THEMES)

/** Theme เริ่มต้นของ user-modern */
export const getDefaultTheme = (): ThemeMetadata => {
  return AVAILABLE_THEMES['huay-today-blue']
}
